import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import {
  Menu,
  FileText,
  Users,
  ShoppingCart,
  Truck,
  Package,
  ArrowUpDown,
  Receipt,
  UserCog,
  Settings,
  ScrollText,
  HardDrive,
  LogOut,
  RotateCcw,
  Undo2,
  X,
  BarChart3,
  LayoutDashboard,
  Plus,
  ChevronDown,
  Square,
} from 'lucide-react';
import { Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuthContext } from '@/context/AuthContext';
import { useInvalidateAuth } from '@/hooks/useAuth';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import { ForcePasswordBanner } from '@/components/ProtectedRoute';
import GlobalSearchBox from '@/components/GlobalSearchBox';

// ── Types ─────────────────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  icon: React.ElementType;
  to?: string; // undefined = disabled (future phase)
  adminOnly?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
}

// ── Nav config ────────────────────────────────────────────────────────────────

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Countertop',
    items: [{ label: 'Countertop Jobs', icon: Square, to: '/countertop-jobs' }],
  },
  {
    title: 'Sales',
    items: [
      { label: 'Estimates', icon: FileText, to: '/estimates' },
      { label: 'Customers', icon: Users, to: '/customers' },
      { label: 'Returns', icon: RotateCcw, to: '/returns' },
    ],
  },
  {
    title: 'Purchasing',
    items: [
      { label: 'Purchase Orders', icon: ShoppingCart, to: '/purchase-orders' },
      { label: 'Suppliers', icon: Truck, to: '/suppliers' },
      { label: 'Purchase Returns', icon: Undo2, to: '/purchase-returns' },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { label: 'Items', icon: Package, to: '/items' },
      { label: 'Stock Adjustments', icon: ArrowUpDown, to: '/stock-adjustments', adminOnly: true },
    ],
  },
  {
    title: 'Finance',
    adminOnly: true,
    items: [{ label: 'Expenses', icon: Receipt, to: '/expenses' }],
  },
  {
    title: 'Reports',
    adminOnly: true,
    items: [{ label: 'Reports', icon: BarChart3, to: '/reports' }],
  },
  {
    title: 'Admin',
    adminOnly: true,
    items: [
      { label: 'Users', icon: UserCog, to: '/users' },
      { label: 'Settings', icon: Settings, to: '/settings' },
      { label: 'Audit Log', icon: ScrollText, to: '/admin/audit-log' },
      { label: 'Backups', icon: HardDrive, to: '/admin/backup' },
    ],
  },
];

// ── User avatar initials ──────────────────────────────────────────────────────

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// ── Sidebar nav item ──────────────────────────────────────────────────────────

function SidebarNavItem({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const Icon = item.icon;

  if (!item.to) {
    // Disabled — future phase
    return (
      <span
        className={cn(
          'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm',
          'text-slate-500 opacity-50 cursor-not-allowed pointer-events-none',
        )}
        aria-disabled="true"
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate">{item.label}</span>
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors duration-150',
          isActive
            ? 'bg-slate-700 text-white font-medium'
            : 'text-slate-300 hover:bg-slate-700/60 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Amber ledger-line accent on active item */}
          <span
            className={cn(
              'absolute left-0 w-0.5 h-5 rounded-r-full bg-amber-400 transition-opacity duration-150',
              isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-30',
            )}
            aria-hidden="true"
          />
          <Icon className="h-4 w-4 shrink-0" />
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

// ── Sidebar section ───────────────────────────────────────────────────────────

function SidebarSection({ section, onNavigate }: { section: NavSection; onNavigate?: () => void }) {
  return (
    <div>
      <p className="px-3 mb-1 text-[10px] font-semibold tracking-widest uppercase text-slate-500 select-none">
        {section.title}
      </p>
      <ul className="space-y-0.5">
        {section.items.map((item) => (
          <li key={item.label} className="relative">
            <SidebarNavItem item={item} onNavigate={onNavigate} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Sidebar content ───────────────────────────────────────────────────────────

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuthContext();

  const isAdmin = user?.role === 'admin';
  const visibleSections = NAV_SECTIONS.filter((s) => !s.adminOnly || isAdmin)
    .map((s) => ({ ...s, items: s.items.filter((item) => !item.adminOnly || isAdmin) }))
    .filter((s) => s.items.length > 0);

  return (
    <div className="flex flex-col h-full">
      {/* Logo / brand */}
      <div className="h-14 flex items-center px-4 border-b border-slate-700/60 shrink-0">
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2 group">
          <span className="h-6 w-6 rounded bg-amber-400 flex items-center justify-center shrink-0">
            <span className="text-slate-900 text-[10px] font-black leading-none">EF</span>
          </span>
          <span className="text-white font-semibold text-sm tracking-tight">Estflow</span>
        </Link>
      </div>

      {/* Nav sections */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5 scrollbar-thin">
        {/* Dashboard — top-level, no section header */}
        <ul className="space-y-0.5">
          <li className="relative">
            <SidebarNavItem
              item={{ label: 'Dashboard', icon: LayoutDashboard, to: '/' }}
              onNavigate={onNavigate}
            />
          </li>
        </ul>
        {visibleSections.map((section) => (
          <SidebarSection key={section.title} section={section} onNavigate={onNavigate} />
        ))}
      </nav>
    </div>
  );
}

// ── User menu (topbar, top-right) ──────────────────────────────────────────────

function UserMenu() {
  const { user } = useAuthContext();
  const invalidateAuth = useInvalidateAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape while open.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function handleLogout() {
    setOpen(false);
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // ignore — session may already be gone
    }
    invalidateAuth();
    navigate('/login', { replace: true });
  }

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2 h-9 pl-1 pr-1.5 sm:pr-2 rounded-md border border-transparent',
          'hover:bg-accent transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          open && 'bg-accent',
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
      >
        {/* Avatar */}
        <span className="h-7 w-7 rounded-full bg-amber-400 flex items-center justify-center shrink-0">
          <span className="text-slate-900 text-xs font-bold leading-none">
            {user ? getInitials(user.full_name) : '?'}
          </span>
        </span>
        <span className="hidden sm:block max-w-[10rem] truncate text-sm font-medium text-foreground">
          {user?.full_name ?? '—'}
        </span>
        <ChevronDown
          className={cn(
            'hidden sm:block h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 w-56 z-50 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
        >
          {/* Identity header */}
          <div className="px-3 py-2.5 border-b border-border">
            <p className="text-sm font-medium truncate">{user?.full_name ?? '—'}</p>
            <span
              className={cn(
                'inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded leading-tight uppercase tracking-wide',
                user?.role === 'admin'
                  ? 'bg-blue-500/15 text-blue-600'
                  : 'bg-slate-200 text-slate-600',
              )}
            >
              {user?.role ?? ''}
            </span>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ── AppShell ──────────────────────────────────────────────────────────────────

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);

  function closeMobile() {
    setMobileOpen(false);
  }

  return (
    <div className="min-h-screen bg-background">
      {/* ── Fixed topbar ─────────────────────────────────────────────────────── */}
      <header className="fixed top-0 inset-x-0 h-14 z-30 flex items-center gap-3 px-4 bg-background border-b border-border">
        {/* Mobile hamburger */}
        <button
          type="button"
          className="md:hidden p-1.5 rounded-md hover:bg-accent transition-colors"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Spacer on desktop (sidebar takes w-60) */}
        <div className="hidden md:block w-60 shrink-0" aria-hidden="true" />

        {/* Search */}
        <div className="flex-1 flex items-center">
          <GlobalSearchBox />
        </div>

        {/* Always-available primary action */}
        <Link to="/estimates/new" className="shrink-0">
          <Button size="sm" className="h-9">
            <Plus className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">New Estimate</span>
          </Button>
        </Link>

        {/* User menu */}
        <UserMenu />
      </header>

      {/* ── Fixed desktop sidebar ─────────────────────────────────────────────── */}
      <aside className="hidden md:flex fixed top-0 left-0 bottom-0 w-60 z-20 flex-col bg-slate-900">
        <SidebarContent />
      </aside>

      {/* ── Mobile overlay sidebar ────────────────────────────────────────────── */}
      {/* Backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}
      {/* Drawer — inert when closed so keyboard focus cannot reach hidden items */}
      <aside
        id="mobile-nav"
        className={cn(
          'md:hidden fixed top-0 left-0 bottom-0 w-72 z-50 flex flex-col bg-slate-900',
          'transition-transform duration-200 ease-in-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Navigation"
        {...((!mobileOpen ? { inert: '' } : {}) as React.HTMLAttributes<HTMLElement>)}
      >
        {/* Close button */}
        <button
          type="button"
          className="absolute top-3 right-3 p-1.5 rounded-md text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          onClick={closeMobile}
          aria-label="Close navigation"
        >
          <X className="h-5 w-5" />
        </button>
        <SidebarContent onNavigate={closeMobile} />
      </aside>

      {/* ── Main content area (banner + page) ─────────────────────────────────── */}
      {/* Banner appears below the fixed header, pushes main content down */}
      <div className="md:ml-60 mt-14">
        <ForcePasswordBanner />
        <main className="p-6 min-h-screen">
          <Outlet />
        </main>
      </div>

      {/* Sonner toast container */}
      <Toaster richColors position="bottom-right" />
    </div>
  );
}
